import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Truck, Users2, Container, Table2, FileText,
  Bell, ShieldCheck, UserCog, Building2, Settings, LogOut, Menu, X, Landmark, Sparkles, BarChart3, ArrowLeftRight,
  CarFront, Package, Hash, BookOpen, FileSignature, Database,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { AlertBell } from "@/components/AlertBell";
import { CompanySwitch } from "@/components/CompanySwitch";
import { GlobalSearch } from "@/components/GlobalSearch";
import { HelpMenu } from "@/components/HelpMenu";
import { api } from "@/lib/api";

const ROLE_LABELS = {
  dueno: "Dueño",
  administracion: "Administración",
  operacion: "Operación",
  consulta: "Consulta",
};

const MENU = [
  { to: "/", label: "Resumen", icon: LayoutDashboard, mod: "dashboard", end: true },
  { to: "/loads", label: "Viajes", icon: Truck, mod: "loads" },
  { to: "/cruces", label: "Cruces", icon: ArrowLeftRight, mod: "cruces" },
  { to: "/unidades", label: "Unidades", icon: CarFront, mod: "unidades" },
  { to: "/cajas", label: "Cajas", icon: Package, mod: "cajas" },
  { to: "/clients", label: "Clientes", icon: Users2, mod: "clients" },
  { to: "/carriers", label: "Carriers", icon: Container, mod: "carriers" },
  { to: "/rate-cards", label: "Tarifario", icon: Table2, mod: "rate_cards" },
  { to: "/referencias-cummins", label: "Ref. Cummins", icon: Hash, mod: "referencias_cummins" },
  { to: "/documents", label: "Documentos", icon: FileText, mod: "documents" },
  { to: "/reports", label: "Reportes", icon: BarChart3, mod: "reports" },
  { to: "/quickbooks", label: "QuickBooks", icon: Landmark, mod: "quickbooks", show: (can, user) => can("loads", "ver") && can("loads", "ver_ganancia") },
  { to: "/facturacion-qbo", label: "Facturación QBO", icon: FileSignature, mod: "facturacion_qbo" },
  { to: "/alerts", label: "Alertas", icon: Bell, mod: "alerts" },
  { to: "/audit", label: "Auditoría", icon: ShieldCheck, mod: "audit" },
  { to: "/users", label: "Usuarios", icon: UserCog, mod: "users" },
  { to: "/companies", label: "Compañías", icon: Building2, mod: "companies" },
  { to: "/guias", label: "Guías", icon: BookOpen, mod: "guias" },
  { to: "/integracion-dat", label: "Integración DAT", icon: Database, mod: "settings", show: (can, user) => user?.rol === "dueno" },
  { to: "/config", label: "Configuración", icon: Settings, mod: "settings" },
];

export default function AppLayout() {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [modoDemo, setModoDemo] = useState(false);

  useEffect(() => {
    const fetchModoDemo = () => api.get("/settings").then(({ data }) => setModoDemo(!!data.modo_demo)).catch(() => {});
    fetchModoDemo();
    window.addEventListener("trebol:settings-updated", fetchModoDemo);
    return () => window.removeEventListener("trebol:settings-updated", fetchModoDemo);
  }, []);

  const items = MENU.filter((m) => (m.show ? m.show(can, user) : can(m.mod, "ver")) || user?.rol === "dueno");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center gap-2 border-b border-border px-5">
        <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground font-heading font-bold">
          T
        </div>
        <span className="font-heading text-lg font-bold tracking-tight text-foreground">
          Trébol<span className="text-primary">·</span>AL
        </span>
      </div>
      <nav className="flex-1 overflow-y-auto p-3 space-y-1">
        {items.map((m) => {
          const Icon = m.icon;
          return (
            <NavLink
              key={m.to}
              to={m.to}
              end={m.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${m.mod}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-green-50 text-green-700 border-l-4 border-primary pl-2"
                    : "text-slate-600 hover:bg-slate-100 border-l-4 border-transparent pl-2"
                }`
              }
            >
              <Icon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
              {m.label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );

  return (
    <div className="flex h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden w-64 shrink-0 border-r border-border bg-white lg:block">
        <SidebarContent />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64 bg-white shadow-xl">
            <button
              className="absolute right-3 top-4 text-slate-500"
              onClick={() => setOpen(false)}
              data-testid="close-drawer-btn"
            >
              <X className="h-5 w-5" />
            </button>
            <SidebarContent />
          </div>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        {modoDemo && (
          <div className="flex h-8 shrink-0 items-center justify-center gap-2 bg-amber-400 text-xs font-semibold text-amber-950"
               data-testid="demo-mode-banner">
            <Sparkles className="h-3.5 w-3.5" /> Datos de ejemplo — entorno de demostración, no es información real de clientes
          </div>
        )}
        <header className="flex flex-wrap items-center gap-3 border-b border-border bg-white px-4 py-3 lg:px-6">
          <button
            className="text-slate-600 lg:hidden"
            onClick={() => setOpen(true)}
            data-testid="open-drawer-btn"
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="order-3 w-full lg:order-none lg:w-auto lg:max-w-sm lg:flex-1">
            <GlobalSearch />
          </div>
          <div className="ml-auto flex items-center gap-3">
            <CompanySwitch />
            <AlertBell />
            <div className="hidden text-right leading-tight sm:block">
              <div className="text-sm font-semibold text-foreground" data-testid="header-username">
                {user?.nombre_completo}
              </div>
              <div className="text-xs text-primary font-medium" data-testid="header-role">
                {ROLE_LABELS[user?.rol] || user?.rol}
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="logout-btn"
              className="gap-2"
            >
              <LogOut className="h-4 w-4" /> Salir
            </Button>
          </div>
        </header>
        <main className="min-w-0 flex-1 overflow-auto p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
      <HelpMenu />
    </div>
  );
}
